import { DatePipe, NgForOf, NgIf } from '@angular/common';
import { Component, computed, effect, inject, Input, OnDestroy, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Badge } from 'primeng/badge';
import { Button } from 'primeng/button';
import { Divider } from 'primeng/divider';
import { InputNumber } from 'primeng/inputnumber';
import { InputText } from 'primeng/inputtext';
import { OverlayBadge } from 'primeng/overlaybadge';
import { Select } from 'primeng/select';
import { Textarea } from 'primeng/textarea';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { catchError, finalize, map, of, Subject, switchMap, takeUntil } from 'rxjs';
import {
  CreateTimeEntryDto,
  TimeEntriesService,
  TimesheetEntryDto,
  ThresholdEntryPreviewRequest,
  WorkItemDto,
  UpdateTimeEntryRequest,
} from '../../../../api';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { ErrorHandlerService } from '../../../../shared/services/error-handler.service';
import { MessageService } from '../../../../shared/services/message.service';
import { DateTimeUtils } from '../../../../shared/utils/date-time-utils';
import { TimeTrackerDayModalData } from '../../model/time-tracker-day-modal-data';
import { WorkItemsFacadeService } from '../../../work-items/services/work-items-facade.service';

@Component({
  selector: 'app-time-tracker-day-modal',
  standalone: true,
  imports: [
    ModalComponent,
    Badge,
    Button,
    DatePipe,
    Divider,
    InputNumber,
    InputText,
    NgForOf,
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    OverlayBadge,
    Select,
    Textarea,
    ToggleSwitch,
  ],
  templateUrl: './time-tracker-day-modal.component.html',
  styleUrl: './time-tracker-day-modal.component.css',
})
export class TimeTrackerDayModalComponent implements OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly timeEntriesService = inject(TimeEntriesService);
  private readonly workItemsFacadeService = inject(WorkItemsFacadeService);
  private readonly messageService = inject(MessageService);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly destroy$ = new Subject<void>();
  private lastHandledWorkItemsRefresh = 0;

  private _data?: TimeTrackerDayModalData;

  readonly dayEntries = signal<TimesheetEntryDto[]>([]);
  readonly loadingEntries = signal(false);
  readonly saving = signal(false);
  readonly selectedEntryId = signal<string | null>(null);
  readonly workItemOptions = computed(() => this._data?.workItems ?? []);
  readonly selectedEntry = computed(
    () => this.dayEntries().find((entry) => entry.id === this.selectedEntryId()) ?? null,
  );
  readonly displayedEntryEarnings = computed(() => {
    const preview = this.entryEarningsPreview();
    if (preview !== undefined) {
      return preview;
    }

    return this.resolveEntryEarnings(this.selectedEntry() ?? undefined);
  });
  readonly dayTotalDuration = computed(() =>
    this.dayEntries().reduce((acc, entry) => acc + (entry.duration ?? 0), 0),
  );
  readonly dayTotalEarnings = computed(() =>
    this.dayEntries().reduce((acc, entry) => acc + (this.resolveEntryEarnings(entry) ?? 0), 0),
  );
  readonly isOvernight = signal(false);
  readonly entryEarningsPreview = signal<number | null | undefined>(undefined);
  private readonly entryEarningsPreviewRefresh$ = new Subject<void>();

  private previousTimeValues: Record<'start' | 'end', string> = {
    start: '',
    end: '',
  };

  readonly timeEntryForm = this.fb.group({
    workItemId: this.fb.control<string | null>(null, {
      validators: [Validators.required],
    }),
    dateIso: this.fb.control<string>('', {
      validators: [Validators.required],
    }),
    start: this.fb.control<string>('', {
      validators: [Validators.required, Validators.pattern(/^([01]\d|2[0-3]):[0-5]\d$/)],
    }),
    end: this.fb.control<string>('', {
      validators: [Validators.required, Validators.pattern(/^([01]\d|2[0-3]):[0-5]\d$/)],
    }),
    duration: this.fb.control<number | null>(60, {
      validators: [Validators.required, Validators.min(1)],
    }),
    billable: this.fb.control<boolean | null>(true, {}),
    revenueAmount: this.fb.control<number | null>(null, {
      validators: [Validators.required, Validators.min(0)],
    }),
    description: this.fb.control<string | null>(null, {
      validators: [Validators.maxLength(300)],
    }),
  });

  @Input()
  set data(value: TimeTrackerDayModalData | undefined) {
    this._data = value;
    if (!value) {
      return;
    }

    this.selectedEntryId.set(value.selectedEntryId ?? null);
    this.resetDraft();
    this.reloadEntries(value.selectedEntryId ?? null);
  }

  get data(): TimeTrackerDayModalData | undefined {
    return this._data;
  }

  get modalTitle(): string {
    if (!this._data) {
      return 'Wpisy trackera';
    }

    const day = this.toLocalDate(this._data.dateIso);
    const dayLabel = day.toLocaleDateString('pl-PL', {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

    return `Tracker - ${dayLabel}`;
  }

  constructor() {
    this.timeEntryForm.controls.workItemId.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateRevenueValidators();
        this.refreshEntryEarningsPreview();
      });

    this.timeEntryForm.controls.billable.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateRevenueValidators();
        this.refreshEntryEarningsPreview();
      });

    this.timeEntryForm.controls.start.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.recalculateDerivedFields();
        this.refreshEntryEarningsPreview();
      });

    this.timeEntryForm.controls.end.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.recalculateDerivedFields();
        this.refreshEntryEarningsPreview();
      });

    this.timeEntryForm.controls.revenueAmount.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshEntryEarningsPreview());

    this.timeEntryForm.controls.dateIso.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshEntryEarningsPreview());

    this.entryEarningsPreviewRefresh$
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.fetchEntryEarningsPreview()),
      )
      .subscribe({
        next: (value) => this.entryEarningsPreview.set(value),
        error: (err) => this.errorHandler.handleError(err),
      });

    effect(() => {
      const refreshToken = this.workItemsFacadeService.refresh();
      if (!this._data || refreshToken === this.lastHandledWorkItemsRefresh) {
        return;
      }

      this.lastHandledWorkItemsRefresh = refreshToken;
      this.reloadEntries(this.selectedEntryId());
    });

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected selectEntry(entry: TimesheetEntryDto): void {
    if (!entry.id) {
      return;
    }

    this.selectedEntryId.set(entry.id);
    this.entryEarningsPreview.set(undefined);
    this.patchFormFromEntry(entry);

    if (entry.editableByCurrentUser === false) {
      this.timeEntryForm.disable({ emitEvent: false });
    } else {
      this.timeEntryForm.enable({ emitEvent: false });
    }
  }

  protected startNewEntry(): void {
    this.selectedEntryId.set(null);
    this.entryEarningsPreview.set(undefined);
    this.resetDraft();
  }

  protected onSave(): void {
    if (!this._data || this.timeEntryForm.invalid || this.saving()) {
      return;
    }

    const formValue = this.timeEntryForm.getRawValue();
    const dayData = this._data;
    const dateIsoValue = formValue.dateIso;
    const startValue = formValue.start;
    const endValue = formValue.end;
    const durationValue = formValue.duration;
    const revenueAmountValue = formValue.revenueAmount;

    if (!dateIsoValue || !startValue || !endValue) {
      return;
    }

    const entryDate = this.toLocalDate(dateIsoValue);
    const resolved = this.resolveTimeValuesForSave(entryDate, startValue, endValue, durationValue);
    if (!resolved.duration || !resolved.endTime) {
      return;
    }

    const workItemId = formValue.workItemId ?? dayData.workItemId ?? this.getDefaultWorkItemId();
    if (!workItemId) {
      return;
    }

    this.saving.set(true);

    if (this.selectedEntry()?.id) {
      const selectedEntry = this.selectedEntry()!;
      if (selectedEntry.editableByCurrentUser === false) {
        this.saving.set(false);
        return;
      }

      const payload: UpdateTimeEntryRequest = {
        id: selectedEntry.id,
        workItemId,
        description: formValue.description ?? undefined,
        billable: formValue.billable ?? undefined,
        revenueAmount: this.isThresholdBillingWorkItem(workItemId)
          ? Number(revenueAmountValue)
          : undefined,
        start: DateTimeUtils.toUTCISO(entryDate, startValue),
        end: resolved.endTime!,
        duration: resolved.duration!,
      };

      this.timeEntriesService
        .updateTimeEntryDetails(dayData.workspaceId, dayData.userId, payload)
        .pipe(
          finalize(() => {
            this.saving.set(false);
          }),
          takeUntil(this.destroy$),
        )
        .subscribe({
          next: () => {
            this.messageService.showSuccess('Wpis trackera został zaktualizowany.');
            this._data?.onChanged?.();
            this.reloadEntries(selectedEntry.id);
          },
          error: (err) => this.errorHandler.handleError(err),
        });
      return;
    }

    const payload: CreateTimeEntryDto = {
      workItemId,
      duration: resolved.duration,
      revenueAmount: this.isThresholdBillingWorkItem(workItemId)
        ? Number(revenueAmountValue)
        : undefined,
      startTime: DateTimeUtils.toUTCISO(entryDate, startValue),
      endTime: resolved.endTime,
    };

    this.timeEntriesService
      .createTimeEntry(dayData.workspaceId, dayData.userId, payload)
      .pipe(
        switchMap((createdEntry) => {
          const description = formValue.description?.trim();
          if (!description) {
            return of(createdEntry);
          }

          const updatePayload: UpdateTimeEntryRequest = {
            id: createdEntry.id!,
            workItemId,
            description,
            billable: formValue.billable ?? undefined,
            revenueAmount: this.isThresholdBillingWorkItem(workItemId)
              ? Number(revenueAmountValue)
              : undefined,
            start: DateTimeUtils.toUTCISO(entryDate, startValue),
            end: resolved.endTime!,
            duration: resolved.duration!,
          };

          return this.timeEntriesService
            .updateTimeEntryDetails(dayData.workspaceId, dayData.userId, updatePayload)
            .pipe(map(() => createdEntry));
        }),
        finalize(() => {
          this.saving.set(false);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (createdEntry) => {
          this.messageService.showSuccess('Dodano wpis trackera.');
          this._data?.onChanged?.();
          this.reloadEntries(createdEntry.id ?? null);
          this.startNewEntry();
        },
        error: (err) => this.errorHandler.handleError(err),
      });
  }

  protected onDeleteSelectedEntry(): void {
    if (
      !this._data ||
      !this.selectedEntry()?.id ||
      this.selectedEntry()?.editableByCurrentUser === false
    ) {
      return;
    }

    const entryId = this.selectedEntry()!.id!;
    this.saving.set(true);

    this.timeEntriesService
      .deleteTimeEntry(this._data.workspaceId, this._data.userId, [entryId])
      .pipe(
        finalize(() => {
          this.saving.set(false);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          this.messageService.showInfo('Usunięto wpis.');
          this._data?.onChanged?.();
          this.reloadEntries();
          this.startNewEntry();
        },
        error: (err) => this.errorHandler.handleError(err),
      });
  }

  protected formatDuration(minutes?: number | null): string {
    if (!minutes) {
      return '0 min';
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours === 0) {
      return `${remainingMinutes} min`;
    }

    if (remainingMinutes === 0) {
      return `${hours} h`;
    }

    return `${hours} h ${remainingMinutes} min`;
  }

  protected formatMoney(value?: number | null): string {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value ?? 0);
  }

  protected resolveEntryEarnings(entry?: TimesheetEntryDto | null): number | null {
    if (!entry) {
      return null;
    }

    return entry.entryEarnings ?? entry.earnings ?? null;
  }

  protected isThresholdBillingWorkItem(workItemId?: string | null): boolean {
    if (!workItemId) {
      return false;
    }

    return this.workItemOptions().find((item) => item.id === workItemId)?.billingMode ===
      WorkItemDto.BillingModeEnum.RevenueThreshold;
  }

  protected isHourlyBillingWorkItem(workItemId?: string | null): boolean {
    if (!workItemId) {
      return false;
    }

    return this.workItemOptions().find((item) => item.id === workItemId)?.billingMode ===
      WorkItemDto.BillingModeEnum.HourlyRate;
  }

  protected isThresholdBillingEntry(entry: TimesheetEntryDto): boolean {
    return this.isThresholdBillingWorkItem(entry.workItemId);
  }

  protected isHourlyBillingEntry(entry: TimesheetEntryDto): boolean {
    return this.isHourlyBillingWorkItem(entry.workItemId);
  }

  protected trackByEntryId(_: number, entry: TimesheetEntryDto): string | undefined {
    return entry.id;
  }

  protected formatTimeRange(start?: string | null, end?: string | null): string {
    if (!start || !end) {
      return '--:--';
    }

    return `${this.formatTime(start)} - ${this.formatTime(end)}`;
  }

  protected onTimeClick(controlName: 'start' | 'end', $event: Event): void {
    const control = this.timeEntryForm.get(controlName);
    if (!control) {
      return;
    }

    this.previousTimeValues[controlName] = control.value ?? '';

    const input = $event.target as HTMLInputElement;
    input.value = input.value.replace(':', '');
    input.select();
  }

  protected onChangeTimeEntry(controlName: 'start' | 'end'): void {
    const control = this.timeEntryForm.get(controlName);
    if (!control) {
      return;
    }

    let value: string = control.value?.trim() ?? '';
    value = value.replace(':', '');

    if (!/^\d{3,4}$/.test(value)) {
      control.setValue(this.previousTimeValues[controlName], { emitEvent: false });
      this.recalculateDerivedFields();
      return;
    }

    if (value.length === 3) {
      value = '0' + value;
    }

    const hours = Number(value.slice(0, 2));
    const minutes = Number(value.slice(2, 4));

    if (hours > 23 || minutes > 59) {
      control.setValue(this.previousTimeValues[controlName], { emitEvent: false });
      this.recalculateDerivedFields();
      return;
    }

    const normalized = `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}`;

    control.setValue(normalized, { emitEvent: false });
    this.recalculateDerivedFields();
  }

  protected canSaveCurrentSelection(): boolean {
    if (!this._data || this.saving()) {
      return false;
    }

    if (this.selectedEntry()?.id && this.selectedEntry()?.editableByCurrentUser === false) {
      return false;
    }

    return !this.timeEntryForm.invalid;
  }

  protected isSelectedEntry(entry: TimesheetEntryDto): boolean {
    return this.selectedEntryId() === entry.id;
  }

  protected get activeWorkItem(): WorkItemDto | null {
    return null;
  }

  protected formatWorkItemType(type?: WorkItemDto.TypeEnum | null): string {
    switch (type) {
      case WorkItemDto.TypeEnum.Contract:
        return 'Kontrakt';
      case WorkItemDto.TypeEnum.Service:
        return 'Usługa';
      case WorkItemDto.TypeEnum.Project:
        return 'Projekt';
      default:
        return 'Nieokreślony';
    }
  }

  protected formatBillingMode(mode?: WorkItemDto.BillingModeEnum | null): string {
    switch (mode) {
      case WorkItemDto.BillingModeEnum.HourlyRate:
        return 'Stawka godzinowa';
      case WorkItemDto.BillingModeEnum.RevenueThreshold:
        return 'Próg przychodu';
      case WorkItemDto.BillingModeEnum.None:
      default:
        return 'Brak';
    }
  }

  protected get displayWorkItemNote(): string | null {
    return this.activeWorkItem?.note ?? null;
  }

  protected get revenueFieldLabel(): string {
    return 'Kwota przychodu';
  }

  protected get showRevenueField(): boolean {
    const workItemId =
      this.timeEntryForm.controls.workItemId.value ??
      this.selectedEntry()?.workItemId ??
      this._data?.workItemId ??
      null;

    return this.isThresholdBillingWorkItem(workItemId);
  }

  protected isReadOnlySelection(): boolean {
    return this.selectedEntry()?.editableByCurrentUser === false;
  }

  private reloadEntries(selectedEntryId: string | null = null): void {
    if (!this._data) {
      return;
    }

    this.loadingEntries.set(true);
    this.timeEntriesService
      .getDayEntries(this._data.workspaceId, this._data.userId, this._data.dateIso)
      .pipe(
        finalize(() => {
          this.loadingEntries.set(false);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (entries) => {
          this.dayEntries.set(entries);

          const targetEntryId = selectedEntryId ?? this._data?.selectedEntryId ?? null;
          if (targetEntryId) {
            const selected = entries.find((entry) => entry.id === targetEntryId);
            if (selected) {
              this.selectEntry(selected);
              return;
            }
          }

          this.startNewEntry();
        },
        error: (err) => this.errorHandler.handleError(err),
      });
  }

  private resetDraft(): void {
    this.timeEntryForm.reset({
      workItemId: this.getDefaultWorkItemId(),
      dateIso: this._data?.dateIso ?? '',
      start: '08:00',
      end: '09:00',
      duration: 60,
      billable: true,
      revenueAmount: null,
      description: null,
    });
    this.timeEntryForm.enable({ emitEvent: false });
    this.updateRevenueValidators();
    this.recalculateDerivedFields();
    this.refreshEntryEarningsPreview();
  }

  private patchFormFromEntry(entry: TimesheetEntryDto): void {
    const start = entry.start ? this.toLocalTime(entry.start) : '08:00';
    const end = entry.end ? this.toLocalTime(entry.end) : this.addMinutesToTime(start, entry.duration ?? 60);

    this.timeEntryForm.patchValue(
      {
        workItemId: entry.workItemId ?? this._data?.workItemId ?? this.getDefaultWorkItemId(),
        dateIso: entry.start ? this.toDateInputValue(new Date(entry.start)) : this._data?.dateIso ?? '',
        start,
        end,
        duration: entry.duration ?? 60,
        billable: entry.billable ?? true,
        revenueAmount: entry.revenueAmount ?? null,
        description: entry.description ?? null,
      },
      { emitEvent: false },
    );
    this.updateRevenueValidators();
    this.recalculateDerivedFields();
    this.refreshEntryEarningsPreview();
  }

  private recalculateDerivedFields(): void {
    const start = this.timeEntryForm.controls.start.value;
    const end = this.timeEntryForm.controls.end.value;

    if (!start || !end) {
      return;
    }

    const startMinutes = this.parseTimeToMinutes(start);
    const endMinutes = this.parseTimeToMinutes(end);

    if (startMinutes == null || endMinutes == null) {
      return;
    }

    let durationMinutes = endMinutes - startMinutes;
    if (durationMinutes < 0) {
      durationMinutes += 24 * 60;
      this.isOvernight.set(true);
    } else {
      this.isOvernight.set(false);
    }

    this.timeEntryForm.controls.duration.setValue(durationMinutes, { emitEvent: false });
  }

  private updateRevenueValidators(): void {
    const revenueControl = this.timeEntryForm.controls.revenueAmount;
    if (this.isThresholdBillingWorkItem(this.timeEntryForm.controls.workItemId.value)) {
      revenueControl.setValidators([Validators.required, Validators.min(0)]);
    } else {
      revenueControl.clearValidators();
    }

    revenueControl.updateValueAndValidity({ emitEvent: false });
  }

  private refreshEntryEarningsPreview(): void {
    this.entryEarningsPreviewRefresh$.next();
  }

  private fetchEntryEarningsPreview() {
    const workItemId =
      this.timeEntryForm.controls.workItemId.value ??
      this.selectedEntry()?.workItemId ??
      this._data?.workItemId ??
      null;

    if (!workItemId || !this.isThresholdBillingWorkItem(workItemId)) {
      return of(undefined);
    }

    if (this.timeEntryForm.controls.billable.value === false) {
      return of(0);
    }

    const revenueAmount = this.timeEntryForm.controls.revenueAmount.value;
    const start = this.timeEntryForm.controls.start.value;
    if (!this._data || revenueAmount == null || !start) {
      return of(null);
    }

    const parsedRevenueAmount = Number(revenueAmount);
    if (!Number.isFinite(parsedRevenueAmount) || parsedRevenueAmount < 0) {
      return of(null);
    }

    const request: ThresholdEntryPreviewRequest = {
      entryId: this.selectedEntry()?.id ?? undefined,
      workItemId,
      startTime: DateTimeUtils.toUTCISO(
        this.toLocalDate(this.timeEntryForm.controls.dateIso.value || this._data.dateIso),
        start,
      ),
      revenueAmount: parsedRevenueAmount,
    };

    return this.timeEntriesService
      .previewThresholdEntry(this._data.workspaceId, this._data.userId, request)
      .pipe(
        map((response) => response.estimatedEntryEarnings ?? null),
        catchError((err) => {
          this.errorHandler.handleError(err);
          return of(undefined);
        }),
      );
  }

  private getDefaultWorkItemId(): string | null {
    if (this._data?.workItemId) {
      return this._data.workItemId;
    }

    if ((this._data?.workItems?.length ?? 0) === 1) {
      return this._data?.workItems?.[0]?.id ?? null;
    }

    return null;
  }

  private parseTimeToMinutes(value: string | null | undefined): number | null {
    if (!value || !/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) {
      return null;
    }

    const [hours, minutes] = value.split(':').map(Number);
    return hours * 60 + minutes;
  }

  private resolveTimeValuesForSave(
    entryDate: Date,
    startValue: string,
    endValue: string,
    durationValue: number | null,
  ): { duration: number | null; endTime: string | null } {
    const startMinutes = this.parseTimeToMinutes(startValue);
    const endMinutes = this.parseTimeToMinutes(endValue);
    if (startMinutes == null || endMinutes == null) {
      return { duration: null, endTime: null };
    }

    let durationMinutes = endMinutes - startMinutes;
    const endDate = new Date(entryDate);

    if (durationMinutes < 0) {
      durationMinutes += 24 * 60;
      endDate.setDate(endDate.getDate() + 1);
    }

    const [hours, minutes] = endValue.split(':').map(Number);
    endDate.setHours(hours, minutes, 0, 0);

    return {
      duration: durationValue ?? durationMinutes,
      endTime: endDate.toISOString(),
    };
  }

  private toLocalDate(dateIso: string): Date {
    const [year, month, day] = dateIso.split('-').map(Number);
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  private toDateInputValue(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private addMinutesToTime(start: string, duration: number): string {
    const [hours, minutes] = start.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + duration;
    const normalized = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
    const resultHours = Math.floor(normalized / 60);
    const resultMinutes = normalized % 60;
    return `${resultHours.toString().padStart(2, '0')}:${resultMinutes.toString().padStart(2, '0')}`;
  }

  private toLocalTime(isoString: string): string {
    const date = new Date(isoString);
    return `${date.getHours().toString().padStart(2, '0')}:${date
      .getMinutes()
      .toString()
      .padStart(2, '0')}`;
  }

  private formatTime(iso: string): string {
    return new Date(iso).toLocaleTimeString('pl-PL', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}
